import { batchEditNodes } from "@builder/store/slices/BuilderSlice/builderSlice";
import store from "@builder/store/store";
import { BuildComponentKey, BuildNode } from "@common/build/types";

const buttonNames: BuildComponentKey[] = [
    "button",
    "printButton",
    "cashButton",
    "terminalButton",
    "tapToPay",
    "vismaButton",
    "customSaleAddToCartButton"
];

const childOf = (nodes: BuildNode[], node: BuildNode, ancestor: BuildComponentKey | BuildComponentKey[]) => {
    const map = new Map(nodes.map(n => [n.id, n]));
    let current: BuildNode | undefined = node;
    const isMatch = (n?: BuildNode) => {
        if (!n) return false;
        if (Array.isArray(ancestor)) return (ancestor as BuildComponentKey[]).includes(n.name as BuildComponentKey);
        return n.name === (ancestor as BuildComponentKey);
    };
    while (current && current.parent) {
        const parent = map.get(current.parent);
        if (isMatch(parent)) return true;
        current = parent as any;
    }
    return false;
};

const directChildOf = (nodes: BuildNode[], node: BuildNode, parent: BuildComponentKey | BuildComponentKey[]) => {
    if (!node.parent) return false;
    const map = new Map(nodes.map(n => [n.id, n]));
    const parentNode = map.get(node.parent);
    if (!parentNode) return false;

    if (Array.isArray(parent)) {
        return (parent as BuildComponentKey[]).includes(parentNode.name as BuildComponentKey);
    }

    return parentNode.name === (parent as BuildComponentKey);
};

const filterByName = (nodes: BuildNode[], names: BuildComponentKey[]) => nodes.filter(n => names.includes(n.name as BuildComponentKey));

const commonGroups = {
    buttons: (nodes: BuildNode[]) => filterByName(nodes, buttonNames),
    radioField: (nodes: BuildNode[]) => filterByName(nodes, ["radioField"]),
    productQuantity: (nodes: BuildNode[]) => filterByName(nodes, ["productQuantity"]),
    customNumpad: (nodes: BuildNode[]) => filterByName(nodes, ["customNumpad"]),
    productVariations: (nodes: BuildNode[]) => filterByName(nodes, ["productVariations"]),
    searchField: (nodes: BuildNode[]) => filterByName(nodes, ["searchField"]),
    dropDownField: (nodes: BuildNode[]) => filterByName(nodes, ["dropdownField"]),
    productGridRepeater: (nodes: BuildNode[]) => nodes.filter(n => n.name === "repeaterDiv" && childOf(nodes, n, ["productGrid"])),
    directChildrenOfRepeaterInProductGrid: (nodes: BuildNode[]) =>
        nodes.filter(n => directChildOf(nodes, n, ["repeaterDiv"]) && childOf(nodes, n, ["productGrid"])),
    orderListRepeater: (nodes: BuildNode[]) => nodes.filter(n => n.name === "repeaterDiv" && childOf(nodes, n, ["orderList"])),
    customerListRepeater: (nodes: BuildNode[]) => nodes.filter(n => n.name === "repeaterDiv" && childOf(nodes, n, ["customerList"])),
    allProductImages: (nodes: BuildNode[]) => nodes.filter(n => n.name === "image" && n.dynamicData === "product.images"),
    productImageInRepeater: (nodes: BuildNode[]) => nodes.filter(n => n.name === "image" && n.dynamicData === "product.images"),
    productImageInRepeaterInOrderData: (nodes: BuildNode[]) => nodes.filter(n => n.name === "image" && n.dynamicData === "product.images" && childOf(nodes, n, ["orderData"])),
    carts: (nodes: BuildNode[]) => nodes.filter(n => n.name === "simpleCart" || n.name === "simpleCartUnfixed"),
    divsThatHaveEitherBGColororBorders: (nodes: BuildNode[]) => nodes.filter(n => n.name === "div" && (n.attr?.style?.borderColor || n.attr?.style?.backgroundColor || n.attr?.style?.borderWidth || n.attr?.style?.border)),
    dynamicGridsAndListsWrappers: (nodes: BuildNode[]) => nodes.filter(n => n.type === "repeater")
} as const;

export type EditingGroup = keyof typeof commonGroups;
export type OnboardThemeEdit = {
    key: EditingGroup;
    update: {
        propertyPath: (string | number)[];
        value: any | { [key: string]: any };
        remove?: boolean;
    };
};

export const applyOnboardThemeEdits = (themeEdits: OnboardThemeEdit[]) => {
   
    const edits = themeEdits.flatMap(({ key, update }) => {
        const filter = commonGroups[key];
        if (!filter) return []; // safeguard
        return [{ filter, update }];
    });

    if (edits.length) {
        store.dispatch(batchEditNodes(edits));
    }
};


// this should be able to revert all the edits too
export const defaultEdits: OnboardThemeEdit[] = [
    { key: "buttons", update: { propertyPath: ["attr", "style"], value: { borderRadius: "8px 8px 8px 8px" } } },
    { key: "productQuantity", update: { propertyPath: ["buttonStyle"], value: { borderRadius: "8px 8px 8px 8px" } } },
    { key: "customNumpad", update: { propertyPath: ["keyStyle"], value: { borderRadius: "8px 8px 8px 8px" } } },

    { key: "productVariations", update: { propertyPath: ["inputStyles"], value: { borderRadius: "8px 8px 8px 8px" } } },
    { key: "searchField", update: { propertyPath: ["attr", "style"], value: { borderRadius: "8px 8px 8px 8px" } } },
    { key: "radioField", update: { propertyPath: ["radioStyle"], value: { borderRadius: "8px 8px 8px 8px" } } },

    { key: "orderListRepeater", update: { propertyPath: ["attr", "style"], value: { borderRadius: "8px 8px 8px 8px", padding: "0px 0px 0px 10px" } } },
    { key: "customerListRepeater", update: { propertyPath: ["attr", "style"], value: { borderRadius: "8px 8px 8px 8px", padding: "0px 0px 0px 10px" } } },

    { key: "carts", update: { propertyPath: ["attr", "style"], value: { borderRadius: "8px 8px 8px 8px" } } },
    { key: "carts", update: { propertyPath: ["customerChip"], value: { borderRadius: "8px 8px 8px 8px" } } },

    { key: "divsThatHaveEitherBGColororBorders", update: { propertyPath: ["attr", "style"], value: { borderRadius: "8px 8px 8px 8px" } } },
    { key: "divsThatHaveEitherBGColororBorders", update: { propertyPath: ["attr", "style", "boxShadow"], value: undefined, remove: true } },
    {
        key: "productGridRepeater",
        update: { propertyPath: ["attr", "style"], value: { borderRadius: "8px 8px 8px 8px", boxShadow: "0px 6px 10px 0px #5E5E5E1A" } }
    },
    { key: "productVariations", update: { propertyPath: ["attr", "style"], value: { borderRadius: "8px 8px 8px 8px" } } },
    { key: "dynamicGridsAndListsWrappers", update: { propertyPath: ["attr", "style"], value: { borderRadius: "8px 8px 8px 8px" } } },
    { key: "allProductImages", update: { propertyPath: ["attr", "style"], value: { borderRadius: "8px 8px 8px 8px", width: "100%", height: "70%" } } },
    { key: "productImageInRepeaterInOrderData", update: { propertyPath: ["attr", "style"], value: { borderRadius: "8px 8px 8px 8px", width: "100px", height: "100px" } } },


];

export function curvedShadowThemify() {
    applyOnboardThemeEdits(defaultEdits);
}
// ---------------------------------------------------------------------------
